import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "378",
  "slug": "how-do-satellites-take-pictures-of-earth",
  "title": "How Do Satellites Actually Take Pictures of Earth?",
  "excerpt": "Satellites don't carry giant cameras snapping photos. Here's how they really capture images of Earth — from orbits and sensors to resolution and revisit times.",
  "date": "2026-06-08",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1770370419338-f9a813302baa?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXRlbGxpdGUlMjBlYXJ0aCUyMG9yYml0fGVufDF8MHx8fDE3ODA5MDYyODh8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Most Earth-observation satellites don't take snapshots like a phone camera. They scan the surface line by line as they orbit, measuring sunlight reflected off the ground in several bands of the spectrum, and the strips are stitched into the images we see.",
  "content": [
    {
      "type": "paragraph",
      "text": "We talk about satellite 'photos' as if a camera in space points down and presses a shutter. The reality is more interesting — and understanding it explains a lot about why satellite imagery looks the way it does, why some images are sharper than others, and why a satellite can revisit your town every few days."
    },
    {
      "type": "heading",
      "text": "Mostly, They Measure Reflected Sunlight"
    },
    {
      "type": "paragraph",
      "text": "Most Earth-observation satellites are 'passive' sensors: they don't light anything up themselves. Instead, they measure sunlight that has bounced off the surface and back up into space. That's why these satellites need daylight and clear skies to produce a useful optical image — clouds block the view, and night leaves nothing to reflect."
    },
    {
      "type": "paragraph",
      "text": "There are also 'active' sensors, most notably radar, which send out their own signal and measure what bounces back. Radar can see through clouds and at night, which is why it's used for monitoring floods and ships. But the familiar, natural-looking imagery in map apps comes mostly from passive optical sensors."
    },
    {
      "type": "heading",
      "text": "Scanning, Not Snapping"
    },
    {
      "type": "paragraph",
      "text": "Rather than capturing a whole scene at once, many imaging satellites build a picture line by line as they move. The satellite races over the surface at thousands of kilometres per hour, and its sensor records a thin strip of ground beneath it, again and again, like a scanner sliding down a page. Those strips are assembled into the long image swaths that get cut up into the tiles you scroll through."
    },
    {
      "type": "paragraph",
      "text": "This is why satellite imagery is captured in 'swaths' — wide ribbons following the satellite's ground track — rather than neat rectangles."
    },
    {
      "type": "heading",
      "text": "Seeing in More Colours Than We Can"
    },
    {
      "type": "paragraph",
      "text": "A phone camera records three bands: red, green, and blue. Many satellites record far more — separate measurements for near-infrared, shortwave infrared, and other slices of the spectrum. Each band is just a grayscale measurement of how much of that particular light the ground reflected."
    },
    {
      "type": "paragraph",
      "text": "To make a 'true colour' image, the red, green, and blue bands are combined the way your eye would see them. To make a 'false colour' image, other bands are swapped in — which is how analysts highlight vegetation, water, or burned land that would be hard to tell apart in natural colour."
    },
    {
      "type": "heading",
      "text": "The Three Resolutions That Matter"
    },
    {
      "type": "paragraph",
      "text": "When people compare satellites, they're usually talking about one of three kinds of resolution:"
    },
    {
      "type": "list",
      "items": [
        "Spatial resolution: how much ground each pixel covers. A 10-metre pixel sees fields and forests; a 30-centimetre pixel can pick out individual cars.",
        "Spectral resolution: how many bands of light the sensor records, and how finely. More bands mean more can be distinguished.",
        "Temporal resolution (revisit time): how often the satellite passes over the same spot — anywhere from twice a day to once every few weeks."
      ]
    },
    {
      "type": "paragraph",
      "text": "There's almost always a trade-off. A satellite that captures very fine detail usually covers a smaller area and revisits less often. One that revisits daily often does so at coarser resolution. No single satellite is best at everything."
    },
    {
      "type": "heading",
      "text": "Orbits: Two Main Strategies"
    },
    {
      "type": "paragraph",
      "text": "How a satellite orbits shapes what it can do. Many imaging satellites use a 'sun-synchronous' polar orbit, circling from pole to pole a few hundred kilometres up while the Earth turns beneath them. A sun-synchronous orbit is designed for a similar local solar time on repeated passes. Coverage depends on the sensor and orbit, and lighting still varies with season."
    },
    {
      "type": "paragraph",
      "text": "Weather satellites often sit much higher, in 'geostationary' orbit about 36,000 kilometres up, where they match the Earth's rotation and hover over the same spot. That's how they produce those looping cloud animations of an entire hemisphere."
    },
    {
      "type": "heading",
      "text": "Why It Helps to Know This"
    },
    {
      "type": "paragraph",
      "text": "Once you know imagery is scanned strip by strip, captured in many bands, and shaped by orbit and resolution, the quirks make sense — the occasional seam in a mosaic, the way colours shift between false-colour maps, the difference between a crisp city view and a blurry one. You start reading images instead of just looking at them."
    },
    {
      "type": "paragraph",
      "text": "That's exactly the skill that makes location-guessing fun. Bring your new understanding of how the picture was made to a round of EarthGuessr and see the ground with fresh eyes."
    }
  ]
};
